import { clamp } from 'lodash-es';
import { DateTime } from 'luxon';
import React from 'react';
import styles from '../GanttChart.module.scss';
import type { HeaderDatePreview } from '../gantt-internal-types';
import type { GanttZoomLevel } from '../gantt-zoom';

interface Props {
  zoom: GanttZoomLevel;
  scrollLeft: number;
  previewDates: HeaderDatePreview | null;
  width: number;
  rowStartDate: string;
}

/**
 * Renders a preview of the dates being manipulated on the Gantt chart.
 * This preview is displayed outside of any virtualized/scrollable context,
 * so we render it absolutely between left: 0 and left: scrollAreaWidth.
 * It should not be shown if we show days, since they show this preview then.
 * */
const GanttChartFloatingDatePreview: React.FC<Props> = ({
  zoom,
  scrollLeft,
  previewDates,
  width: containerWidth,
  rowStartDate,
}) => {
  if (previewDates == null) {
    return null;
  }

  const startDate = DateTime.fromISO(previewDates.startDate);
  const endDate = DateTime.fromISO(previewDates.endDate);

  const startDateIndex = startDate.diff(DateTime.fromISO(rowStartDate), 'days').days;
  const previewLengthInDays = endDate.diff(startDate, 'days').days + 1;

  const left = clamp(startDateIndex * zoom.columnWidth - scrollLeft, 0, containerWidth);
  const width = clamp(
    previewLengthInDays * zoom.columnWidth - zoom.dayPadding,
    zoom.columnWidth,
    containerWidth - left
  );

  let secondDateFormatString;
  if (endDate.month !== startDate.month) {
    secondDateFormatString = 'MMM dd';
  } else {
    secondDateFormatString = 'dd';
  }
  const label = `${startDate.toFormat('MMM dd')} - ${endDate.toFormat(secondDateFormatString)}`;

  return (
    <div
      className={styles.floatingDatePreview}
      style={{
        left,
        width,
      }}
    >
      {label}
    </div>
  );
};
export default GanttChartFloatingDatePreview;
